import {
  createTextTag,
  deleteTextTag,
  getTextTagList,
  updateTextTag,
} from '../../api/textTagApi';
import type { TextTagData } from '../../dto/EntityRepository';
import { CreateTagReq, DeleteTagReq, UpdateTagReq } from '../../dto/ReqDtoRepository';
import { TagListResponse, TagResponse } from '../../dto/ResDtoRepository';
import { useTextTagStore } from '../../store/textTagStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useModalStore } from '../../store/portal/modalStore';
import { useTextViewStore } from '../../store/TextViewStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';

interface UseTextTagStateResult {
  tags: TextTagData[];
  selectedTags: TextTagData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseTextTagActionsResult {
  getTextTagList: () => Promise<void>;
  createTextTag: (data: CreateTagReq) => Promise<void>;
  updateTextTag: (data: UpdateTagReq) => Promise<void>;
  deleteTextTag: (data: DeleteTagReq) => Promise<void>;
  addSelectedTag: (tag: TextTagData) => void;
  removeSelectedTag: (tag: TextTagData) => void;
  clearSelectedTags: () => void;
  clearTags: () => void;
}

type UseTextTagResult = UseTextTagStateResult & UseTextTagActionsResult;

export const useTextTagState = (): UseTextTagStateResult => {
  const tags = useTextTagStore((state) => state.tags);
  const selectedTags = useTextTagStore((state) => state.selectedTags);
  const listOwnerAui = useTextTagStore((state) => state.listOwnerAui);
  const listScope = useTextTagStore((state) => state.listScope);
  const status = useTextTagStore((state) => state.status);

  return { tags, selectedTags, listOwnerAui, listScope, status };
};

export const useTextTagActions = (): UseTextTagActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setTags = useTextTagStore((s) => s.setTags);
  const beginTextTagListFetch = useTextTagStore((s) => s.beginTextTagListFetch);
  const setTextTagListError = useTextTagStore((s) => s.setTextTagListError);
  const afterCreateTag = useTextTagStore((s) => s.afterCreateTag);
  const afterUpdateTag = useTextTagStore((s) => s.afterUpdateTag);
  const afterDeleteTag = useTextTagStore((s) => s.afterDeleteTag);
  const addSelectedTag = useTextTagStore((s) => s.addSelectedTag);
  const removeSelectedTag = useTextTagStore((s) => s.removeSelectedTag);
  const clearSelectedTags = useTextTagStore((s) => s.clearSelectedTags);
  const clearTags = useTextTagStore((s) => s.clearTags);
  const { setUpdatedTempAlert, setDeletedTempAlert } = useTempAlertStore();
  const { clearModal } = useModalStore();
  const syncTextViewTag = useTextViewStore((s) => s.syncTextTag);
  const removeTextViewTag = useTextViewStore((s) => s.removeTextTag);
  const withApiHandler = useApiWrapper();

  const handleGetTextTagListSuccess = (
    response: TagListResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    setTags(response.data, ownerAui, scope);
  };

  const handleCreateTextTagSuccess = (response: TagResponse) => {
    afterCreateTag(response.data);
    setUpdatedTempAlert();
    clearModal();
  };

  const handleUpdateTextTagSuccess = (response: TagResponse) => {
    afterUpdateTag(response.data);
    syncTextViewTag(response.data);
    setUpdatedTempAlert();
  };

  const handleDeleteTextTagSuccess = (tagId: number) => {
    afterDeleteTag(tagId);
    removeTextViewTag(tagId);
    setDeletedTempAlert();
  };

  const fetchTextTagList = async (aui: string) => {
    beginTextTagListFetch(aui, 'public');
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => getTextTagList(aui),
        (response) => handleGetTextTagListSuccess(response, aui, 'public'),
      );
    } catch (error) {
      setTextTagListError();
      throw error;
    }
  };

  const handleTextTagRequest = async (
    action: 'get' | 'create' | 'update' | 'delete',
    data?: CreateTagReq | UpdateTagReq | DeleteTagReq,
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'create':
          return runCurrentArchiveRequest(
            aui,
            () => createTextTag(aui, data as CreateTagReq),
            handleCreateTextTagSuccess,
          );
        case 'update':
          return runCurrentArchiveRequest(
            aui,
            () => updateTextTag(aui, data as UpdateTagReq),
            handleUpdateTextTagSuccess,
          );
        case 'delete': {
          const tagId = (data as DeleteTagReq).tagId;
          return runCurrentArchiveRequest(
            aui,
            () => deleteTextTag(aui, tagId),
            () => handleDeleteTextTagSuccess(tagId),
          );
        }
        case 'get':
        default:
          return fetchTextTagList(aui);
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  return {
    getTextTagList: () => handleTextTagRequest('get'),
    createTextTag: (data: CreateTagReq) => handleTextTagRequest('create', data),
    updateTextTag: (data: UpdateTagReq) => handleTextTagRequest('update', data),
    deleteTextTag: (data: DeleteTagReq) => handleTextTagRequest('delete', data),
    addSelectedTag,
    removeSelectedTag,
    clearSelectedTags,
    clearTags,
  };
};

/** 기존 호출부 호환: state + actions */
export const useTextTag = (): UseTextTagResult => ({
  ...useTextTagState(),
  ...useTextTagActions(),
});
